import { cloneDeep, isEmpty as _isEmpty, isNumber } from "lodash";
import { FACEBOOK_CTA_IDS } from "social/constants/CTA";
import { TIKTOK_DISCLAIMER_TYPES } from "social/constants/common";
import {
	DYNAMIC_SOCIAL_AD_TYPES,
	SOCIAL_AD_TYPES,
	SOCIAL_FORMATS
} from "utils/constants";
import {
	IMPOSIUM_COMP_CREATION_METHOD,
	IMPOSIUM_PROJECT_CREATION_METHOD
} from 'widgets/social2/constants/common';
import { PINTEREST_NO_DISCLOSURE } from "social/validators/pinterest";

export function isEmpty(value) {
	if (typeof value === "string") { // Check for empty strings
		return value.trim().length === 0;
	}

	if (isNumber(value) || typeof value === "boolean") { // Allow numbers and booleans
		return false;
	}

	return _isEmpty(value); // Use lodash utility for objects
}

export function removeNewlines(value){
	if(typeof(value) === "string"){
		// Replace newlines without any adjacent spaces with a space
		let stripped = value.replace(/(?<![ ])[\r\n](?![ ])/g, " ");

		// strip remaining newlines
		return stripped.replace(/[\r\n]/g, "");
	}
	return value;
}

export function defaultSanitizer(values) {
	return Object.keys(values).reduce((prev, key) => {
		const value = values[key];
		if (isEmpty(value)) {
			return {
				...prev,
				[key]: null,
			};
		}

		return {
			...prev,
			[key]: value,
		};
	}, {});
}

export function dynamicSanitizer(values) {
	return Object.keys(values).reduce((prev, key) => {
		const value = values[key];

		if(value?.hasOwnProperty?.("value")){
			let finalValue = value.value;
			if(isEmpty(value?.value)){
				finalValue = null;
			}

			return {
				...prev,
				[key]:{
					...value,
					value:finalValue,
				},
			};
		}	//end if(value.hasProperty(value))

		return {
			...prev,
			[key]: value,
		};
	}, {});
}

function defaultSerializer(values, adType, socialState, sanitize=defaultSanitizer){
	let { name, ...socialValues } = values[adType] || {};
	socialValues = sanitize(cloneDeep(socialValues));
	const result = {
		instantAdType: "s",
		adType,
		name,
		socialValues,
	};
	processImposiumEntities(result, socialValues);
	processAfterEffectsEntities(result, socialValues);
	return result;
}

function processImposiumEntities(creativeValues, socialValues){
	let project = socialValues.imposiumProject;
	if (project){
		if (project.creationMethod === IMPOSIUM_PROJECT_CREATION_METHOD.EXISTING_PROJECT){
			creativeValues.storyId = project.id;
		} else if (project.creationMethod === IMPOSIUM_PROJECT_CREATION_METHOD.NEW_PROJECT){
			creativeValues.newProjectName = project.newProjectName;
		}
		delete socialValues.imposiumProject;
	}
	const composition = socialValues.imposiumComposition;
	if (composition){
		if (composition.creationMethod === IMPOSIUM_COMP_CREATION_METHOD.EXISTING_COMPOSITION && composition.id) {
			creativeValues.compositionId = composition.id;
		}
		delete socialValues.imposiumComposition;
	}

	const { cards, placements } = socialValues;

	if (cards && cards.length > 0){
		let project = cards[0].imposiumProject;
		if (project){
			if (project.creationMethod === IMPOSIUM_PROJECT_CREATION_METHOD.EXISTING_PROJECT){
				creativeValues.storyId = project.id;
			} else if (project.creationMethod === IMPOSIUM_PROJECT_CREATION_METHOD.NEW_PROJECT){
				creativeValues.newProjectName = project.newProjectName;
			}
			delete cards[0].imposiumProject;
		}
		cards.forEach((card) => {
			delete card.selectedCompositionId;
			const composition = card.imposiumComposition;
			if(composition) {
				if (composition.creationMethod === IMPOSIUM_COMP_CREATION_METHOD.EXISTING_COMPOSITION) {
					card.compositionId = composition.id;
				}
				delete card.imposiumComposition;
			}
		});
	}

	if (placements){
		Object.values(placements).forEach((placement) => {
			delete placement.selectedCompositionId;
			const composition = placement.imposiumComposition;
			if (composition?.creationMethod === IMPOSIUM_COMP_CREATION_METHOD.EXISTING_COMPOSITION) {
				placement.compositionId = composition.id;
			}
			delete placement.imposiumComposition;
		});
	}
	//clean up form field values
	delete socialValues.selectedCompositionId;
	delete socialValues.newProjectName;
	delete socialValues.selectedStoryId;
}

function processAfterEffectsEntities(creativeValues, socialValues){
	const aePackage = socialValues.afterEffectsPackage;
	if (aePackage){
		creativeValues.packageId = aePackage.id;
		delete socialValues.afterEffectsPackage;
	}

	const composition = socialValues.afterEffectsComposition;
	if (composition){
		creativeValues.aeCompositionId = composition.id;
		delete socialValues.afterEffectsComposition;
	}

	const { cards, placements } = socialValues;

	if (cards && cards.length > 0){
		const aePackage = cards[0].afterEffectsPackage;
		if (aePackage){
			creativeValues.packageId = aePackage.id;
			delete cards[0].afterEffectsPackage;
		}

		cards.forEach((card) => {
			delete card.aeCompositionId;
			const composition = card.afterEffectsComposition;
			if(composition) {
				card.aeCompositionId = composition.id;
				delete card.afterEffectsComposition;
			}
		});
	}

	if (placements){
		Object.values(placements).forEach((placement) => {
			delete placement.aeCompositionId;
			const composition = placement.afterEffectsComposition;
			if (composition) {
				placement.aeCompositionId = composition.id;
				delete placement.afterEffectsComposition;
			}
		});
	}

	if (socialValues.frameNumber !== null && socialValues.frameNumber !== undefined) {
		creativeValues.frameNumber = socialValues.frameNumber;
	}
	delete socialValues.frameNumber;
	delete socialValues.aeCompositionId;
	delete socialValues.packageId;
}

const defaultDynamicSerializer = makeDynamic(defaultSerializer);
const defaultDynamicCarouselSerializer = makeDynamic(defaultCarouselSerializer);

function makeDynamic(serializer){
	return function(values, adType, socialState, sanitize=defaultSanitizer){
		const serialized = serializer(values, adType, socialState, sanitize);

		const { isDynamic, assetType, ...socialValues } = serialized.socialValues || {};
		const instantAdType = isDynamic ? "d" : "s";

		return {
			...serialized,
			instantAdType,
			assetType,
			isDynamic,
			socialValues,
		};
	};
}

export function defaultCarouselSerializer(values, adType, socialState, sanitize = defaultSanitizer){
	let serialized = defaultSerializer(values, adType, socialState, sanitize);
	/*
		Cloning prevents us from causing problems when removing the clientId, etc.,
		e.g. If you submit and the submit fails because of a concept name collision,
		you don't want the serializer to mutate the data and cause problems with rendering.
	*/
	let { cards } = cloneDeep(serialized.socialValues || {});

	if(cards){
		delete serialized.socialValues?.currentCard;

		cards = cards.map((card) => {
			const sanitizedCard = sanitize(card);
			if(sanitizedCard.type === SOCIAL_FORMATS.IMAGE){
				delete sanitizedCard.video;
			}
			delete sanitizedCard.clientId;
			return sanitizedCard;
		});

		serialized.socialValues.cards = cards;
	}


	return serialized;
}

export function serializeGoogleDemandGenCarousel(values, adType, socialState, sanitize = defaultSanitizer){
	let serialized = defaultCarouselSerializer(values, adType, socialState, sanitize);

	let { cards } = serialized.socialValues || {};
	const { requiredAspectRatios = {} } = serialized.socialValues || {};

	if(cards){
		cards.forEach((card) => {
			card.images = Object.entries(card.images || {})
				.filter(([aspectRatio]) => requiredAspectRatios[aspectRatio]) // Only keep relevant values
				.map(([aspectRatio, image]) => ({ // Add `aspectRatio` to image objects
					...image,
					aspectRatio,
				}));

			if(card.cta){
				const cardLocale = card.language?.id || "en";
				const cardCTA = socialState.googleLocales.locales[cardLocale]?.ctas?.find(cta => cta.name === card.cta);

				card.cta = {
					...cardCTA,
					locale: cardLocale,
				};
			}

			delete card.language;
		});
	}

	serialized.socialValues.cards = cards;
	/*
		requiredAspectRatios is a hash like:
		{
			square: true,
			vertical: false,
		}

		We need to serialize it as an array of just the required ratio names.
		e.g. ["square"]
	*/
	serialized.socialValues.aspectRatios = Object.keys(requiredAspectRatios).filter(ratio => requiredAspectRatios[ratio]);

	delete serialized.socialValues?.currentCard;
	delete serialized.socialValues?.requiredAspectRatios;

	return serialized;
}

export function buildMOCTA(cta){
	let mappedCTA = { ...cta };

	if(cta){
		mappedCTA = {
			...cta,
			name:cta.value,
			id:FACEBOOK_CTA_IDS[cta.value],
		};
	}

	return mappedCTA;
}

export function serializeFacebook(values, adType, socialState, sanitize=defaultSanitizer){
	let serialized = defaultSerializer(values, adType, socialState, sanitize);
	let {
		cta,
		placements,
		cards,
		headline,
		primaryText,
	} = serialized.socialValues || {};

	if(cta){
		let id = FACEBOOK_CTA_IDS[cta];
		if(id){
			serialized.socialValues.cta = {
				id,
				name: cta,
			};
		}
	}

	// Remove end spaces in headline and primaryText
	if (headline) {
		serialized.socialValues.headline = headline.trim();
	}
	if (primaryText) {
		serialized.socialValues.primaryText = primaryText.trim();
	}

	if(placements) {
		const serializedPlacements = Object.values(placements).map((placement) => {
			const serializedPlacement = { ...placement };
			delete serializedPlacement._isNew;

			return sanitize(serializedPlacement);
		});
		if (serializedPlacements.length) {
			serialized.socialValues.placements = serializedPlacements;
		}
	}

	if(cards){
		delete serialized.socialValues?.currentCard;

		// delete seeMore fields if not relevant
		const seeMoreCard = serialized.socialValues?.seeMoreCard;
		if(!seeMoreCard){
			delete serialized.socialValues?.seeMoreDisplayLink;
			delete serialized.socialValues?.seeMoreDisplayUrl;
		}

		cards = cards.map((card) => {
			const sanitizedCard = sanitize(card);
			if(sanitizedCard.type === SOCIAL_FORMATS.IMAGE){
				delete sanitizedCard.video;
			}
			delete sanitizedCard.clientId;
			return sanitizedCard;
		});

		serialized.socialValues.cards = cards;
	}

	delete serialized.socialValues?._currentActivePAC;
	delete serialized.socialValues?.["_select-aspect-ratios"];

	return serialized;
}

export function serializeFacebookMO(values, adType, socialState, sanitize=defaultSanitizer){
	let serialized = defaultDynamicSerializer(values, adType, socialState, sanitize);
	let {
		placements,
		cards,
		headline,
		primaryText,
		disclaimerEnabled,
		disclaimerText,
		disclaimerUrl,
		disclaimerId,
	} = serialized.socialValues || {};

	serialized.socialValues.disclaimer = disclaimerEnabled ? {
		id: disclaimerId,
		text: disclaimerText || null,
		url: disclaimerUrl || null,
	} : null;
	delete serialized.socialValues.disclaimerEnabled;
	delete serialized.socialValues.disclaimerText;
	delete serialized.socialValues.disclaimerUrl;
	delete serialized.socialValues.disclaimerId;

	// Remove end spaces in headline and primaryText
	if (headline) {
		serialized.socialValues.headline.value = headline.value?.trim() || null;
	}
	if (primaryText) {
		serialized.socialValues.primaryText.value = primaryText.value?.trim() || null;
	}

	serialized.socialValues.cta = buildMOCTA(serialized.socialValues.cta);


	if(placements) {
		const serializedPlacements = Object.values(placements).map((placement) => {
			const serializedPlacement = { ...placement };
			delete serializedPlacement._isNew;

			return sanitize(serializedPlacement);
		});
		if (serializedPlacements.length) {
			serialized.socialValues.placements = serializedPlacements;
		} else {
			serialized.socialValues.placements = [];
		}
	}

	if(cards){
		delete serialized.socialValues?.currentCard;

		// delete seeMore fields if not relevant
		const seeMoreCard = serialized.socialValues?.seeMoreCard;
		if(!seeMoreCard){
			delete serialized.socialValues?.seeMoreDisplayLink;
			delete serialized.socialValues?.seeMoreDisplayUrl;
		}

		cards = cards.map((card) => {
			const sanitizedCard = sanitize(card);
			if(sanitizedCard.type === SOCIAL_FORMATS.IMAGE){
				delete sanitizedCard.video;
			}
			delete sanitizedCard.clientId;
			return sanitizedCard;
		});

		serialized.socialValues.cards = cards;
	}

	delete serialized.socialValues?._currentActivePAC;
	delete serialized.socialValues?.["_select-aspect-ratios"];

	return serialized;
}

export function serializeLinkedIn(values, adType, socialState, sanitize=defaultSanitizer){
	let serialized = defaultDynamicSerializer(values, adType, socialState, sanitize);
	serialized.socialValues.intro = {
		...serialized.socialValues.intro,
		value: removeNewlines(serialized.socialValues.intro?.value),
	};
	return serialized;
}

export function serializeLinkedInCarousel(values, adType, socialState, sanitize=defaultSanitizer){
	let serialized = defaultDynamicCarouselSerializer(values, adType, socialState, sanitize);
	serialized.socialValues.introDescription = {
		...serialized.socialValues.introDescription,
		value: removeNewlines(serialized.socialValues.introDescription?.value),
	};
	return serialized;
}

export function serializeSnapchat(values, adType, socialState, sanitize=defaultSanitizer){
	let serialized = defaultDynamicSerializer(values, adType, socialState, sanitize);
	let {
		disclaimerEnabled,
		disclaimerName,
		disclaimerText,
	} = serialized.socialValues || {};
	serialized.socialValues.disclaimerName = disclaimerEnabled ? disclaimerName : null;
	serialized.socialValues.disclaimerText = disclaimerEnabled ? disclaimerText : null;
	delete serialized.socialValues.disclaimerEnabled;
	return serialized;
}

export function serializePinterest(values, adType, socialState, sanitize=defaultSanitizer){
	let serialized = defaultDynamicSerializer(values, adType, socialState, sanitize);
	let {
		disclosureEnabled,
		disclosureType,
		disclosureUrl,
	} = serialized.socialValues || {};
	serialized.socialValues.disclosureType = {
		value: disclosureEnabled ? disclosureType : PINTEREST_NO_DISCLOSURE,
		isDynamic: false,
		dynVarName: "",
	};
	serialized.socialValues.disclosureUrl = disclosureEnabled ? {
		value: disclosureUrl,
		isDynamic: false,
		dynVarName: "",
	} : null;
	delete serialized.socialValues.disclosureEnabled;
	return serialized;
}

export function serializeGoogleDemandGenImage(values, adType, socialState, sanitizer=defaultSanitizer) {
	let serialized = defaultSerializer(values, adType, socialState, sanitizer);
	let {
		headlines,
		descriptions,
		images,
		logos,
		language,
		cta,
	} = serialized.socialValues || {};

	function serializeTextFieldArray(field) {
		let populatedFields = field.filter(val => !isEmpty(val));

		if(populatedFields.length) {
			return populatedFields.map(val => ({ name: val }));
		}

		return null;
	}

	function serializeImageFieldArray(field){
		return field.map((val) => {
			const { name, id } = val;

			return {
				name,
				id,
			};
		});
	}

	if(headlines) {
		serialized.socialValues.headlines = serializeTextFieldArray(headlines);
	}

	if(descriptions) {
		serialized.socialValues.descriptions = serializeTextFieldArray(descriptions);
	}

	if(images) {
		serialized.socialValues.images = serializeImageFieldArray(images);
	}

	if(logos) {
		serialized.socialValues.logos = serializeImageFieldArray(logos);
	}

	if(cta){
		const locale = language?.id || "en";
		const ctaObj = socialState.googleLocales.locales[locale]?.ctas?.find(fullCTA => fullCTA.name === cta);
		serialized.socialValues.cta = {
			...ctaObj,
			locale,
		};

		delete serialized.socialValues.language;
	}

	return serialized;
}

export function serializeSnapchatStoryCarousel(values, adType, socialState, sanitizer= defaultSanitizer){
	const serialized = defaultSerializer(values, adType, socialState, sanitizer);
	let {
		disclaimerEnabled,
		disclaimerName,
		disclaimerText,
	} = serialized.socialValues || {};
	serialized.socialValues.disclaimerName = disclaimerEnabled ? disclaimerName : null;
	serialized.socialValues.disclaimerText = disclaimerEnabled ? disclaimerText : null;
	delete serialized.socialValues.disclaimerEnabled;

	const {
		discoveryHeadline,
		discoveryBackgroundImage,
		discoveryLogoImage,
		...restSerializedSV
	} = serialized.socialValues || {};

	serialized.socialValues = {
		...restSerializedSV,
		discovery: {
			headline: discoveryHeadline,
			backgroundImage: discoveryBackgroundImage,
			logoImage: discoveryLogoImage,
		},
	};
	return serialized;
}

function buildTikTokDisclaimer(socialValues) {
	const {
		disclaimerEnabled,
		disclaimerType,
		disclaimerText,
		clickableOptions,
		...restSocialValues
	} = socialValues;

	if (!disclaimerEnabled) {
		return { ...restSocialValues, disclaimer: null };
	}

	const disclaimer = { disclaimerType };

	if (disclaimerType === TIKTOK_DISCLAIMER_TYPES.STANDARD) {
		disclaimer.disclaimerText = disclaimerText;
	} else {
		disclaimer.clickableOptions = (clickableOptions || [])
			.map(({ id, ...rest }) => rest);
	}

	return { ...restSocialValues, disclaimer };
}

export function serializeTikTokVideo(values, adType, socialState, sanitize = dynamicSanitizer) {
	const serialized = defaultDynamicSerializer(values, adType, socialState, sanitize);
	serialized.socialValues = buildTikTokDisclaimer(serialized.socialValues || {});
	serialized.socialValues.primaryText = {
		...serialized.socialValues.primaryText,
		value: removeNewlines(serialized.socialValues.primaryText?.value),
	};

	return serialized;
}

export function serializeTwitter(values, adType, socialState, sanitize = dynamicSanitizer) {
	const serialized = defaultSerializer(values, adType, socialState, sanitize);
	serialized.socialValues.tweet = removeNewlines(serialized.socialValues.tweet);

	return serialized;
}

export function serializeReddit(values, adType, socialState, sanitize=defaultSanitizer){
	let serialized = defaultDynamicSerializer(values, adType, socialState, sanitize);
	let {
		disclaimerEnabled,
		disclaimerText,
	} = serialized.socialValues || {};
	serialized.socialValues.disclaimerText = disclaimerEnabled ? disclaimerText : null;
	delete serialized.socialValues.disclaimerEnabled;
	return serialized;
}

const SERIALIZERS_BY_ADTYPE = { /*
		Sanitization occurs within defaultSerializer. This removes any empty values ("", [], {}, etc) from
		being passed through to the api. When adding a cusotm serializer, make sure to either first pass your
		values through defaultSerializer or sanitize your values via defaultSanitizer or some custom
		sanitization function.
	*/
	[SOCIAL_AD_TYPES.FACEBOOK_IMAGE]: serializeFacebook,
	[SOCIAL_AD_TYPES.FACEBOOK_VIDEO]: serializeFacebook,
	[SOCIAL_AD_TYPES.FACEBOOK_CAROUSEL]: serializeFacebook,
	[SOCIAL_AD_TYPES.FACEBOOK_IMAGE_MO]: serializeFacebookMO,
	[SOCIAL_AD_TYPES.FACEBOOK_VIDEO_MO]: serializeFacebookMO,
	[SOCIAL_AD_TYPES.FACEBOOK_CAROUSEL_MO]: serializeFacebookMO,
	[SOCIAL_AD_TYPES.GOOGLE_DEMAND_GEN_CAROUSEL]: serializeGoogleDemandGenCarousel,
	[SOCIAL_AD_TYPES.GOOGLE_DEMAND_GEN_IMAGE]: serializeGoogleDemandGenImage,
	[SOCIAL_AD_TYPES.PINTEREST_IMAGE]: serializePinterest,
	[SOCIAL_AD_TYPES.PINTEREST_VIDEO]: serializePinterest,
	[SOCIAL_AD_TYPES.PINTEREST_MAX_WIDTH_VIDEO]: serializePinterest,
	[SOCIAL_AD_TYPES.PINTEREST_CAROUSEL]: defaultDynamicCarouselSerializer,
	[SOCIAL_AD_TYPES.LINKEDIN_IMAGE]: serializeLinkedIn,
	[SOCIAL_AD_TYPES.LINKEDIN_VIDEO]: serializeLinkedIn,
	[SOCIAL_AD_TYPES.LINKEDIN_CAROUSEL]: serializeLinkedInCarousel,
	[SOCIAL_AD_TYPES.SNAPCHAT_IMAGE]: serializeSnapchat,
	[SOCIAL_AD_TYPES.SNAPCHAT_VIDEO]: serializeSnapchat,
	[SOCIAL_AD_TYPES.SNAPCHAT_CAROUSEL]: serializeSnapchatStoryCarousel,
	[SOCIAL_AD_TYPES.TIKTOK_VIDEO]: serializeTikTokVideo,
	[SOCIAL_AD_TYPES.TWITTER_IMAGE]: serializeTwitter,
	[SOCIAL_AD_TYPES.TWITTER_VIDEO]: serializeTwitter,
	[SOCIAL_AD_TYPES.REDDIT_IMAGE]: serializeReddit,
	[SOCIAL_AD_TYPES.REDDIT_VIDEO]: serializeReddit,
};

export function serializeByAdType(values, adType, socialState) {
	const isDynamicType = DYNAMIC_SOCIAL_AD_TYPES.includes(adType);

	const fallbackSerializer = isDynamicType ? defaultDynamicSerializer : defaultSerializer;
	const sanitize = isDynamicType ? dynamicSanitizer : defaultSanitizer;

	const serializer = SERIALIZERS_BY_ADTYPE[adType] || fallbackSerializer;
	return serializer(values, adType, socialState, sanitize);
}
